// src/components/paymentTracking/CardDeclinesPanel.js
import React, { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Button,
  IconButton,
  Alert,
  Collapse,
  Divider,
  Grid,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ErrorIcon from "@mui/icons-material/Error";
import EmailIcon from "@mui/icons-material/Email";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import RouterIcon from "@mui/icons-material/Router";
import WarningIcon from "@mui/icons-material/Warning";
import ShieldIcon from "@mui/icons-material/Shield";
import BlockIcon from "@mui/icons-material/Block";
import apiClient from "../../services/EnhancedApiClient";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import { useEnvironment } from "../../context/EnvironmentContext";

const CardDeclinesPanel = () => {
  const [declines, setDeclines] = useState([]);
  const [loading, setLoading] = useState(true);
  // Update the error state handler
  const [error, setError] = useState(null);

  // Function to display alerts
  const renderAlert = () => {
    if (!error) return null;

    if (typeof error === "string") {
      return (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      );
    } else if (typeof error === "object") {
      return (
        <Alert severity={error.severity || "error"} sx={{ my: 2 }}>
          {error.message}
        </Alert>
      );
    }

    return null;
  };
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [clearInProgress, setClearInProgress] = useState(false);
  const [clearAllDialogOpen, setClearAllDialogOpen] = useState(false);
  const [clearAllInProgress, setClearAllInProgress] = useState(false);

  const { environment } = useEnvironment();

  // Enhanced fetchDeclines method for CardDeclinesPanel.js
  const fetchDeclines = useCallback(
    async (force = false) => {
      try {
        console.log(`Fetching card declines for ${environment} environment`);
        if (force) {
          setRefreshing(true);
          // Clear cache when force refreshing
          apiClient.clearCacheForEndpoint("/api/admin/card-declines");
        } else {
          setLoading(true);
        }

        // Wait a short time when forcing to ensure backend has updated
        if (force) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }

        // Add a timestamp query param to bust any potential caching
        const timestamp = Date.now();
        const response = await apiClient.fetchCardDeclines(force, {
          timestamp,
        });
        console.log("Raw decline data:", JSON.stringify(response.declines));

        // If no declines came back but we're expecting some, try one more time
        if (
          force &&
          (!response.declines || response.declines.length === 0) &&
          declines.length > 0
        ) {
          console.log(
            "No declines returned after clearing block, trying again after delay"
          );
          await new Promise((resolve) => setTimeout(resolve, 1000));

          // Try one more time with a new timestamp
          const retryResponse = await apiClient.fetchCardDeclines(true, {
            timestamp: Date.now(),
          });

          if (retryResponse.declines && retryResponse.declines.length > 0) {
            response.declines = retryResponse.declines;
            console.log("Successfully retrieved declines on retry");
          }
        }

        // Sort the declines by timestamp (newest first)
        if (response.declines && response.declines.length > 0) {
          response.declines.sort((a, b) => {
            // Get timestamps - try multiple possible fields
            const getTimestamp = (item) => {
              if (item.timestamp) return new Date(item.timestamp);
              if (item.details?.lastDecline)
                return new Date(item.details.lastDecline);
              if (item.lastDeclineTime) return new Date(item.lastDeclineTime);
              if (item.details?.firstAttempt)
                return new Date(item.details.firstAttempt);
              return new Date(0); // Return epoch if no timestamp found
            };

            const dateA = getTimestamp(a);
            const dateB = getTimestamp(b);

            // Sort newest first (descending)
            return dateB - dateA;
          });
        }

        // Log the block type of the first decline if there is one
        if (response.declines && response.declines.length > 0) {
          console.log(
            "First decline blockType:",
            response.declines[0].blockType
          );
        }

        // Update state with fetched data
        setDeclines(response.declines || []);
        setLastUpdated(
          response.lastUpdated ? new Date(response.lastUpdated) : new Date()
        );
        setError(null);
      } catch (err) {
        console.error("Error fetching card declines:", err);
        setError("Failed to load card declines");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [environment, declines.length]
  ); // Include declines.length to trigger refreshes

  useEffect(() => {
    fetchDeclines(true); // Force refresh when environment changes
  }, [environment, fetchDeclines]); // F

  const handleRefresh = () => {
    fetchDeclines(true);
  };

  const toggleRowExpanded = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const handleClearBlock = (block) => {
    // Make a clean copy of the block
    setSelectedBlock({ ...block });
    setClearDialogOpen(true);
  };

  const confirmClearBlock = async () => {
    if (!selectedBlock) return;

    setClearInProgress(true);
    setError(null);

    try {
      // Skip if this is a pending block - they can't be cleared
      if (
        selectedBlock.blockType === "pending" ||
        (typeof selectedBlock.blockType === "string" &&
          selectedBlock.blockType.toLowerCase() === "pending")
      ) {
        setClearDialogOpen(false);
        setError({
          severity: "warning",
          message:
            "Pending items cannot be cleared as they are not yet blocked",
        });
        setClearInProgress(false);
        setSelectedBlock(null);
        return;
      }

      // Make sure we're working with a clean blockType
      const blockType = selectedBlock.blockType?.toString().toLowerCase() || "";

      // Create a clean block data object with just the necessary properties
      const blockData = {
        blockType: blockType,
      };

      // Add the appropriate identifier based on the block type
      if (blockType.includes("cookie")) {
        blockData.blockType = "cookie";
        blockData.symphonyCookie = selectedBlock.symphonyCookie || selectedBlock.sessionId;
        blockData.identifier = selectedBlock.symphonyCookie || selectedBlock.sessionId;
      } else if (blockType.includes("ip")) {
        blockData.blockType = "ip";
        blockData.ipAddress = selectedBlock.ipAddress;
      } else if (blockType.includes("email")) {
        blockData.blockType = "email";
        blockData.email = selectedBlock.email;
      } else {
        // Unknown block type
        setClearDialogOpen(false);
        setError({
          severity: "error",
          message: `Unknown block type: ${blockType}`,
        });
        setClearInProgress(false);
        setSelectedBlock(null);
        return;
      }

      console.log("Sending block data to clear:", blockData);

      // Call API to clear block
      const result = await apiClient.clearBlock(blockData);
      console.log("Clear block result:", result);

      if (!result || result.status === "error") {
        throw new Error(result?.message || "Unknown error clearing block");
      }

      // Close dialog
      setClearDialogOpen(false);

      // Explicitly clear the API cache
      apiClient.clearCacheForEndpoint("/api/admin/card-declines");

      // First immediate refresh - remove from local state
      setDeclines((prevDeclines) => {
        // Remove the cleared block from local state
        const filteredDeclines = prevDeclines.filter((decline) => {
          // For IP blocks
          if (
            blockData.blockType === "ip" &&
            decline.blockType.toString().toLowerCase().includes("ip")
          ) {
            return decline.ipAddress !== blockData.ipAddress;
          }
          // For email blocks
          else if (
            blockData.blockType === "email" &&
            decline.blockType.toString().toLowerCase().includes("email")
          ) {
            return decline.email !== blockData.email;
          }
          // For cookie/session blocks
          else if (
            blockData.blockType === "cookie" &&
            decline.blockType.toString().toLowerCase().includes("cookie")
          ) {
            return decline.symphonyCookie !== blockData.symphonyCookie &&
              decline.symphonyCookie !== blockData.identifier &&
              decline.sessionId !== blockData.symphonyCookie &&
              decline.sessionId !== blockData.identifier;
          }
          return true; // Keep other blocks
        });

        console.log(
          `Removed block from local state, new count: ${filteredDeclines.length}`
        );
        return filteredDeclines;
      });

      // Show success message
      setError({
        severity: "success",
        message: `Successfully cleared ${blockData.blockType} block AND reset attempts counter`,
      });

      // Delay and then fetch fresh data from the server
      setTimeout(() => {
        fetchDeclines(true);

        // Clear success message after data refreshed
        setTimeout(() => {
          setError(null);
        }, 2000);
      }, 1000);
    } catch (err) {
      console.error("Error clearing block:", err);
      setError({
        severity: "error",
        message: `Failed to clear block: ${err.message || "Unknown error"}`,
      });
    } finally {
      setClearInProgress(false);
      setSelectedBlock(null);
    }
  };

  const handleClearAllBlocks = () => {
    setClearAllDialogOpen(true);
  };

  const confirmClearAllBlocks = async () => {
    setClearAllInProgress(true);
    setError(null);

    try {
      console.log("Attempting to clear all blocks");

      // Call API to clear all blocks
      const result = await apiClient.clearAllBlocks();
      console.log("Clear all blocks result:", result);

      if (!result || result.status === "error") {
        throw new Error(result?.message || "Unknown error clearing all blocks");
      }

      // Show success briefly
      setError({
        severity: "success",
        message: `Successfully cleared ${result.count || "all"} blocks`,
      });

      // Close dialog
      setClearAllDialogOpen(false);

      // Refresh data
      fetchDeclines(true);

      // Clear success message after a delay
      setTimeout(() => {
        setError(null);
      }, 3000);
    } catch (err) {
      console.error("Error clearing all blocks:", err);
      setError({
        severity: "error",
        message: `Failed to clear all blocks: ${
          err.message || "Unknown error"
        }`,
      });
    } finally {
      setClearAllInProgress(false);
    }
  };

  const getBlockTypeIcon = (blockType) => {
    if (!blockType) return <ErrorIcon fontSize="small" />;

    const type = blockType.toString().toLowerCase();
    if (type.includes("cookie")) {
      return <FingerprintIcon fontSize="small" />;
    } else if (type.includes("ip")) {
      return <RouterIcon fontSize="small" />;
    } else if (type.includes("email")) {
      return <EmailIcon fontSize="small" />;
    } else if (type.includes("order")) {
      return <ShoppingCartIcon fontSize="small" />;
    } else if (type === "pending") {
      return <HourglassTopIcon fontSize="small" />;
    }
    return <ErrorIcon fontSize="small" />;
  };

  const getBlockTypeColor = (blockType) => {
    if (!blockType) return "default";

    const type = blockType.toString().toLowerCase();
    if (type.includes("cookie")) {
      return "secondary";
    } else if (type.includes("ip")) {
      return "primary";
    } else if (type.includes("email")) {
      return "warning";
    } else if (type === "pending") {
      return "default";
    }
    return "default";
  };

  const getDisplayBlockType = (blockType) => {
    if (!blockType) return "Unknown";

    const type = blockType.toString().toLowerCase();
    if (type.includes("cookie")) {
      return "Session";
    } else if (type.includes("ip")) {
      return "IP";
    } else if (type.includes("email")) {
      return "Email";
    } else if (type === "pending") {
      return "Pending";
    }

    return blockType.charAt(0).toUpperCase() + blockType.slice(1);
  };

  const formatDate = (dateString) => {
    if (
      !dateString ||
      dateString === "Unknown" ||
      dateString === "Invalid Date"
    ) {
      return "Unknown";
    }

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Unknown";
      }
      return date.toLocaleString();
    } catch (e) {
      return "Unknown";
    }
  };

  return (
    <Paper
      id="panel-card-declines"
      sx={{
        p: 0, // Remove default padding
        mb: 3,
        // Keep the existing orange border
        borderTop: "4px solid #d97706",
        boxShadow:
          "0 -2px 4px rgba(217, 119, 6, 0.1), 0 2px 8px rgba(0,0,0,0.1)",
        overflow: "hidden",
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          backgroundColor: "#fff7ed",
          padding: "20px 24px 16px 24px",
          mb: 0,
        }}
      >
        <Typography variant="h5" component="h2">
          <Box display="flex" alignItems="center" gap={1}>
            <BlockIcon sx={{ fontSize: "1.5em", color: "#d97706" }} />
            <Box>
              <Box component="span" fontWeight="bold">
                Fraud Multiple Attempts Blocks
                {environment === "staging" && (
                  <Chip
                    label="STAGING"
                    size="small"
                    color="warning"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Typography>
        <Box display="flex" alignItems="center">
          {lastUpdated && (
            <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
              Updated: {lastUpdated.toLocaleTimeString()}
            </Typography>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
            size="small"
            sx={{ mr: 1 }}
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={handleClearAllBlocks}
            startIcon={<LockOpenIcon />}
          >
            Clear All Blocks
          </Button>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, pb: 1, backgroundColor: '#fff7ed' }}>
        <Typography sx={{ fontSize: '1rem', fontWeight: 'normal' }} color="text.secondary">
          Blocks user for 20 minutes after 3 card declines in orders using the same IP, Email, or Session.
        </Typography>
      </Box>
      <Box sx={{ px: 3, pb: 3, pt: 2, backgroundColor: "white" }}>
        {renderAlert()}

        {loading && !refreshing ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {declines.length} total blocks and suspicious activity patterns
                (including expired)
              </Typography>
            </Box>

            <TableContainer
              sx={{
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                overflow: "hidden",
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f9fafb" }}>
                    <TableCell
                      padding="checkbox"
                      sx={{ backgroundColor: "#f9fafb" }}
                    ></TableCell>
                    <TableCell
                      sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}
                    >
                      Timestamp
                    </TableCell>
                    <TableCell
                      sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}
                    >
                      Block Type
                    </TableCell>
                    <TableCell
                      sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}
                    >
                      Email
                    </TableCell>
                    <TableCell
                      sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}
                    >
                      IP Address
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}
                    >
                      Attempts
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}
                    >
                      Mode
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {declines.length > 0 ? (
                    declines.map((decline, index) => {
                      const rowId = decline.id || `decline-${index}`;
                      const isExpanded = expandedRows[rowId] || false;

                      return (
                        <React.Fragment key={rowId}>
                          <TableRow
                            hover
                            sx={{
                              bgcolor: decline.expired
                                ? "rgba(0, 0, 0, 0.04)"
                                : decline.isBlocked
                                ? "rgba(211, 47, 47, 0.05)"
                                : "inherit",
                              opacity: decline.expired ? 0.6 : 1,
                            }}
                          >
                            <TableCell padding="checkbox">
                              <IconButton
                                size="small"
                                onClick={() => toggleRowExpanded(rowId)}
                              >
                                {isExpanded ? (
                                  <ExpandLessIcon />
                                ) : (
                                  <ExpandMoreIcon />
                                )}
                              </IconButton>
                            </TableCell>
                            <TableCell component="th" scope="row">
                              {(() => {
                                let timestamp =
                                  decline.details?.lastDecline ||
                                  decline.lastDeclineTime ||
                                  decline.timestamp ||
                                  decline.details?.firstAttempt;

                                if (timestamp) {
                                  return new Date(timestamp).toLocaleString();
                                }
                                return "Unknown";
                              })()}
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={getBlockTypeIcon(decline.blockType)}
                                label={getDisplayBlockType(decline.blockType)}
                                size="small"
                                color={getBlockTypeColor(decline.blockType)}
                              />
                            </TableCell>
                            <TableCell>{decline.email || "Unknown"}</TableCell>
                            <TableCell>
                              <code>{decline.ipAddress || "Unknown"}</code>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={decline.attempts}
                                size="small"
                                color={
                                  decline.attempts >= 5
                                    ? "error"
                                    : decline.attempts >= 3
                                    ? "warning"
                                    : "default"
                                }
                              />
                            </TableCell>
                            <TableCell align="center">
                              {decline.expired ? (
                                <Tooltip title="Block has expired">
                                  <Chip
                                    icon={<LockOpenIcon />}
                                    label="Expired"
                                    size="small"
                                    color="default"
                                    variant="outlined"
                                  />
                                </Tooltip>
                              ) : decline.isBlocked ? (
                                <Chip
                                  icon={<LockIcon />}
                                  label="Blocked"
                                  size="small"
                                  color="error"
                                />
                              ) : (
                                <Chip
                                  label="Warning"
                                  size="small"
                                  color="warning"
                                />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip
                                title={
                                  decline.shadowMode
                                    ? "Shadow Mode"
                                    : "Production"
                                }
                              >
                                {decline.shadowMode ? (
                                  <ShieldIcon
                                    color="warning"
                                    fontSize="small"
                                  />
                                ) : (
                                  <WarningIcon color="error" fontSize="small" />
                                )}
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Clear Block">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleClearBlock(decline)}
                                >
                                  <LockOpenIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>

                          <TableRow>
                            <TableCell
                              colSpan={9}
                              style={{ paddingTop: 0, paddingBottom: 0 }}
                            >
                              <Collapse
                                in={isExpanded}
                                timeout="auto"
                                unmountOnExit
                              >
                                <Box sx={{ py: 2, px: 1 }}>
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                      <Typography
                                        variant="subtitle2"
                                        gutterBottom
                                      >
                                        Block Details
                                      </Typography>
                                      <Box
                                        component="dl"
                                        sx={{
                                          display: "grid",
                                          gridTemplateColumns:
                                            "max-content 1fr",
                                          gap: "4px 12px",
                                        }}
                                      >
                                        <Box
                                          component="dt"
                                          sx={{ fontWeight: "bold" }}
                                        >
                                          Name:
                                        </Box>
                                        <Box component="dd">
                                          {decline.firstName || decline.lastName
                                            ? `${decline.firstName || ""} ${
                                                decline.lastName || ""
                                              }`.trim()
                                            : "Unknown"}
                                        </Box>
                                        <Box
                                          component="dt"
                                          sx={{ fontWeight: "bold" }}
                                        >
                                          First Attempt:
                                        </Box>
                                        <Box component="dd">
                                          {decline.details?.firstAttempt
                                            ? formatDate(
                                                decline.details.firstAttempt
                                              )
                                            : "Unknown"}
                                        </Box>
                                        <Box
                                          component="dt"
                                          sx={{ fontWeight: "bold" }}
                                        >
                                          Last Decline:
                                        </Box>
                                        <Box component="dd">
                                          {decline.details?.lastDecline
                                            ? formatDate(
                                                decline.details.lastDecline
                                              )
                                            : formatDate(
                                                decline.lastDeclineTime
                                              ) || "Unknown"}
                                        </Box>
                                        <Box
                                          component="dt"
                                          sx={{ fontWeight: "bold" }}
                                        >
                                          Error Code:
                                        </Box>
                                        <Box component="dd">
                                          {decline.details?.errorCode ||
                                            decline.errorCode ||
                                            "Unknown"}
                                        </Box>
                                        <Box
                                          component="dt"
                                          sx={{ fontWeight: "bold" }}
                                        >
                                          Block Reason:
                                        </Box>
                                        <Box component="dd">
                                          {decline.details?.blockReason ||
                                            decline.blockReason ||
                                            "Multiple failed payment attempts"}
                                        </Box>
                                        <Box
                                          component="dt"
                                          sx={{ fontWeight: "bold" }}
                                        >
                                          Mode:
                                        </Box>
                                        <Box component="dd">
                                          {decline.shadowMode
                                            ? "Shadow Mode"
                                            : "Production"}
                                        </Box>
                                      </Box>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                      <Typography
                                        variant="subtitle2"
                                        gutterBottom
                                      >
                                        Related Data
                                      </Typography>

                                      {decline.relatedEmails &&
                                        decline.relatedEmails.length > 0 && (
                                          <Box mb={1}>
                                            <Typography
                                              variant="body2"
                                              sx={{ fontWeight: "bold" }}
                                            >
                                              Related Emails:
                                            </Typography>
                                            <Box sx={{ pl: 2 }}>
                                              {decline.relatedEmails.map(
                                                (email, idx) => (
                                                  <div key={idx}>{email}</div>
                                                )
                                              )}
                                            </Box>
                                          </Box>
                                        )}

                                      {decline.relatedOrderNumbers &&
                                        decline.relatedOrderNumbers.length >
                                          0 && (
                                          <Box mb={1}>
                                            <Typography
                                              variant="body2"
                                              sx={{ fontWeight: "bold" }}
                                            >
                                              Related Orders:
                                            </Typography>
                                            <Box sx={{ pl: 2 }}>
                                              {decline.relatedOrderNumbers.map(
                                                (order, idx) => (
                                                  <div key={idx}>{order}</div>
                                                )
                                              )}
                                            </Box>
                                          </Box>
                                        )}

                                      {decline.relatedIPs &&
                                        decline.relatedIPs.length > 0 && (
                                          <Box mb={1}>
                                            <Typography
                                              variant="body2"
                                              sx={{ fontWeight: "bold" }}
                                            >
                                              Related IPs:
                                            </Typography>
                                            <Box sx={{ pl: 2 }}>
                                              {decline.relatedIPs.map(
                                                (ip, idx) => (
                                                  <div key={idx}>
                                                    <code>{ip}</code>
                                                  </div>
                                                )
                                              )}
                                            </Box>
                                          </Box>
                                        )}
                                    </Grid>
                                  </Grid>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={12} align="center">
                        No card declines or fraud blocks detected
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* All dialog components stay the same... */}
        <Dialog
          open={clearDialogOpen}
          onClose={() => setClearDialogOpen(false)}
        >
          <DialogTitle>Clear Block</DialogTitle>
          <DialogContent>
            <Typography variant="body1" paragraph>
              Are you sure you want to clear this block?
            </Typography>
            {selectedBlock && (
              <Box component="div" mt={1}>
                <strong>Block Type:</strong>{" "}
                {getDisplayBlockType(selectedBlock.blockType)}
                <br />
                {selectedBlock.blockType
                  ?.toString()
                  .toUpperCase()
                  .includes("IP") && (
                  <>
                    <strong>IP Address:</strong> {selectedBlock.ipAddress}
                    <br />
                  </>
                )}
                {selectedBlock.blockType
                  ?.toString()
                  .toUpperCase()
                  .includes("EMAIL") && (
                  <>
                    <strong>Email:</strong> {selectedBlock.email}
                    <br />
                  </>
                )}
                {selectedBlock.blockType
                  ?.toString()
                  .toUpperCase()
                  .includes("COOKIE") && (
                  <>
                    <strong>Session:</strong> {selectedBlock.symphonyCookie || selectedBlock.sessionId}
                    <br />
                  </>
                )}
                {selectedBlock.blockType
                  ?.toString()
                  .toUpperCase()
                  .includes("ORDER") && (
                  <>
                    <strong>Order #:</strong> {selectedBlock.orderNumber}
                    <br />
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setClearDialogOpen(false)}
              color="primary"
              disabled={clearInProgress}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmClearBlock}
              color="error"
              variant="contained"
              disabled={clearInProgress}
            >
              {clearInProgress ? "Clearing..." : "Clear Block"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={clearAllDialogOpen}
          onClose={() => setClearAllDialogOpen(false)}
        >
          <DialogTitle>Clear All Blocks</DialogTitle>
          <DialogContent>
            <Typography variant="body1" paragraph>
              Are you sure you want to clear all blocks? This will remove all
              IP, email, session, and order-based blocks.
            </Typography>
            <Box
              component="div"
              mt={1}
              mb={1}
              p={1}
              bgcolor="#fff7ed"
              borderRadius={1}
            >
              <Typography variant="body2" color="warning.dark">
                <strong>Warning:</strong> This action cannot be undone and may
                allow fraudulent users to place orders.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setClearAllDialogOpen(false)}
              color="primary"
              disabled={clearAllInProgress}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmClearAllBlocks}
              color="error"
              variant="contained"
              disabled={clearAllInProgress}
            >
              {clearAllInProgress ? "Clearing..." : "Clear All Blocks"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Paper>
  );
};

export default CardDeclinesPanel;
